import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchDashboard, type AttentionItem } from '../api';
import {
  Empty,
  ErrorBox,
  HealthBadge,
  KpiCard,
  Loading,
  ProgressBar,
  RelativeTime,
  Section,
  Table,
  Value,
} from '../components/ui';

const STATUS_LABEL: Record<string, string> = {
  planning: 'Planificación',
  active: 'Activo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
  maintenance: 'Mantenimiento',
  archived: 'Archivado',
};

const SEVERITY: Record<
  AttentionItem['severity'],
  { label: string; classes: string; icon: string }
> = {
  critical: {
    label: 'Crítico',
    icon: '■',
    classes: 'border-red-300 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30',
  },
  high: {
    label: 'Alto',
    icon: '▲',
    classes: 'border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30',
  },
  medium: {
    label: 'Medio',
    icon: '●',
    classes: 'border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)]',
  },
};

export function DashboardPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    // Refresco conservador: la VPS tiene 2 vCPU compartidos con 15 procesos.
    refetchInterval: 60_000,
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const { kpis, projects, attention, generatedAt } = query.data;

  return (
    <>
      <div className="mb-6 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Dashboard</h1>
          <p className="text-[12px] text-[var(--color-ink-muted)]">
            Datos del espejo de GitHub · actualizado{' '}
            {new Date(generatedAt).toLocaleTimeString('es-ES')}
          </p>
        </div>
      </div>

      <Section title="Indicadores">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {kpis.map(({ key, ...kpi }) => (
            <KpiCard key={key} {...kpi} />
          ))}
        </div>
      </Section>

      <Section
        title="Requiere atención"
        subtitle={
          attention.length === 0
            ? undefined
            : 'Ordenado por urgencia. Lo de arriba debería mirarse hoy.'
        }
      >
        {attention.length === 0 ? (
          <Empty>Nada que requiera atención inmediata.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {attention.map((item, index) => {
              const severity = SEVERITY[item.severity];
              return (
                <li
                  key={`${item.kind}-${item.project ?? index}`}
                  className={`rounded-lg border px-3 py-2 ${severity.classes}`}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="text-[9px] opacity-70">{severity.icon}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                      {severity.label}
                    </span>
                    <span className="text-[13px] font-medium">{item.title}</span>
                    {item.href && (
                      <Link
                        to={item.href}
                        className="ml-auto shrink-0 text-[12px] underline opacity-70 hover:opacity-100"
                      >
                        ver
                      </Link>
                    )}
                  </div>
                  <p className="mt-0.5 pl-[18px] text-[12px] leading-snug opacity-80">
                    {item.detail}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="Estado de los proyectos" subtitle={`${projects.length} proyectos`}>
        <Table
          head={[
            'Proyecto',
            'Estado',
            'Pri.',
            'Salud',
            'Progreso',
            'PRs',
            'CI (30 d)',
            'Commits (30 d)',
            'En prod.',
            'Actividad',
          ]}
        >
          {projects.map((project) => (
            <tr
              key={project.slug}
              className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-surface-muted)]"
            >
              <td className="px-3 py-2">
                <Link to={`/projects/${project.slug}`} className="font-medium hover:underline">
                  {project.name}
                </Link>
                <div className="text-[11px] text-[var(--color-ink-muted)]">
                  {project.repoNames ?? 'sin repositorio'}
                  {project.unmanagedRepoCount > 0 && ' · no gestionado en GitHub'}
                </div>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-[12px]">
                {STATUS_LABEL[project.status] ?? project.status}
              </td>
              <td className="px-3 py-2 text-[12px] tabular-nums">{project.priority}</td>
              <td className="whitespace-nowrap px-3 py-2">
                <HealthBadge health={project.health} reasons={project.healthReasons} />
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <ProgressBar pct={project.progressPct} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                {project.prsOpenHuman}
                {project.prsOpenBot > 0 && (
                  <span className="text-[var(--color-ink-muted)]"> +{project.prsOpenBot} bot</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                {project.runFailurePct30d === null ? (
                  <Value
                    reason={
                      project.runFailurePct === null
                        ? 'el repositorio no tiene CI configurada'
                        : 'sin ejecuciones en los últimos 30 días'
                    }
                  >
                    {null}
                  </Value>
                ) : (
                  <span
                    className={
                      project.runFailurePct30d >= 30 ? 'text-red-700 dark:text-red-400' : undefined
                    }
                    title={
                      project.runFailurePct === null
                        ? undefined
                        : `${project.runFailurePct} % histórico (toda la vida del repositorio)`
                    }
                  >
                    {project.runFailurePct30d} % fallo
                  </span>
                )}
              </td>
              <td className="px-3 py-2 tabular-nums">{project.commits30d}</td>
              <td className="px-3 py-2 tabular-nums">
                {project.tagsProduction > 0 ? (
                  <span title="Tags con sufijo -deployed o -documented">
                    {project.tagsProduction}
                  </span>
                ) : (
                  <Value reason="este proyecto no marca despliegues con tags">{null}</Value>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <RelativeTime iso={project.lastActivityAt} />
              </td>
            </tr>
          ))}
        </Table>
      </Section>
    </>
  );
}
